/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import PropTypes from 'prop-types';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { MultiComboBox, ComboBox } from 'components/selects/ComboBox';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { hasRole } from 'utils/SessionUtil';
import { SetupStep, SetupStepCondition } from 'models/setup/SetupStep';
import { tabs } from 'components/layouts/Tab';
import CommonStepContent from './projectstepmanagement/CommonStepContent';
import SetupStepContent from './projectstepmanagement/SetupStepContent';
import {
  completeProjectStepSetup,
  findProjectStep,
  findProjectStepSetupStatus,
  findProjectStepType,
  validateProjectStepSetup,
} from 'apis/setup/SetupStep';
import { Code } from 'models/common/CommonCode';
import useSessionStore from 'stores/useSessionStore';
import { useLoading } from 'components/process/Loading';
import { IconButton } from 'components/buttons/IconSVG';
import { SubTitleGroup } from 'components/layouts/ContentLayout';
import SetupStepCopyPopup from './projectstepmanagement/SetupStepCopyPopup';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import { checkAvailableProjectManager } from 'apis/setup/SetupProject';

/**
 * @desc 설비셋업관리 > 프로젝트 기준정보 > 프로젝트 셋업단계 설정/조회 > 프로젝트 셋업단계 설정 화면
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.07.07   jshwang12       신규 생성
 * 2026.08.07   jshwang12       설비가 등록된 경우 단계 수정 불가 (가중치 포함)
 * 2026.08.25   jshwang12       프로젝트에 설정된 조직유형코드에 따라 설비군 조회, 단계유형이 없는 경우 셋업 단계 복사를 통한 셋업단계설정 유도
 */
interface PageLocationState {
  elmPrjId?: string;
}
const SetupProjectStepManagementPage = () => {
  const tabCmnRef = useRef<any>();
  const tabStepRef = useRef<any>();
  const conditionRef = useRef<SetupStepCondition>();
  const { t } = useTranslation();
  const { state } = useLocation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const { openLoading } = useLoading();
  const [code, setCode] = useState<any>();
  const [errors, setErrors] = useState<any>({});
  const defaultCondition = {
    bildPlntCds: [],
    elmPrjId: '',
  };
  const [condition, setCondition] = useState<SetupStepCondition>({ ...defaultCondition });
  const [locationState, setLocationState] = useState<PageLocationState>(useLocation().state);
  const [searchParams] = useSearchParams();
  const userSession = useSessionStore();
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const [setupStatus, setSetupStatus] = useState<SetupStep[]>([]); // 단계유형별 설정가능 상태정보 [{spTpCd, eqpYn, prjCpltYn, setgCpltYn}, ..]
  const [orgTpCd, setOrgTpCd] = useState<string>('');
  const [rowData, setRowData] = useState<SetupStep[]>([]);
  const [isOpenCopyPopup, setOpenCopyPopup] = useState<boolean>(false);

  // 단계설정 완료여부 (완료된 경우 셋업단계 복사/설정완료 불가)
  const setgCpltYn = useMemo(() => setupStatus?.[0]?.setgCpltYn || 'Y', [setupStatus]);

  const tabMenus = useMemo(() => {
    return [
      { id: 'CMN', name: t('setup.label.단계 유형/공통단계', '단계 유형/공통단계') },
      { id: 'SP', name: t('setup.label.셋업 단계', '셋업 단계') },
    ];
  }, []);

  const optionsPrjMaster = useMemo(() => {
    return (rowData || [])
      .filter((o) =>
        (condition?.bildPlntCds || []).length > 0
          ? condition.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [rowData, condition?.bildPlntCds]);

  useEffect(() => {
    // 프로젝트ID 기본값 설정
    const defElmPrjId =
      searchParams?.get('elmPrjId') || locationState?.elmPrjId || userSession?.elmPrjId;
    if (window.location.pathname === '/setup/project-step/detail') {
      if (defElmPrjId) {
        handleOnChange('elmPrjId', defElmPrjId);
        handleSearch(defElmPrjId);
      }
    }
    findProjectStep({}).then((result) => {
      setRowData(result || []);
    });
    getCommonCodes();
  }, []);

  useEffect(() => {
    if (locationState?.elmPrjId) {
      const id = locationState?.elmPrjId;
      handleOnChange('elmPrjId', id);
      handleSearch(id);
    }
  }, [locationState]);

  useEffect(() => {
    if (state) {
      setLocationState(state);
    }
  }, [state]);

  useEffect(() => {
    if (conditionRef.current) {
      refreshSetupStatus();
      searchTabContent();
    }
  }, [currentTabIndex]);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('FA_SU_PLNT_CD'), // 플랜트
    ]).then(([bildPlntCd]) => {
      setCode({
        bildPlntCd: bildPlntCd,
      });
    });
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const validate = () => {
    const err = {};
    // 프로젝트명
    if (!condition?.elmPrjId) {
      Object.assign(err, { elmPrjId: true });
    }
    setErrors(err);
    if (!Object.keys(err).filter((k) => err[k]).length) {
      return true;
    }
    return false;
  };

  /**
   * 단계유형별 설정가능 상태정보 조회
   * 저장으로 단계유형이 추가/삭제되면 상태정보도 함께 갱신되어야 하므로
   * 각 탭의 저장 완료 시점에도 호출된다.
   */
  const refreshSetupStatus = async (elmPrjId = '') => {
    const id = elmPrjId || conditionRef.current?.elmPrjId;
    if (!id) {
      setOrgTpCd('');
      setSetupStatus([]);
      return [];
    }
    const status = await findProjectStepSetupStatus(id);
    if ((status || []).length > 0) {
      setOrgTpCd(status[0]?.elmPrjTpCd || '');
      setSetupStatus(status);
    } else {
      setOrgTpCd('');
      setSetupStatus([]);
    }
    return status || [];
  };

  const handleSearch = async (elmPrjId = '') => {
    if (elmPrjId) {
      const params = { elmPrjId: elmPrjId };
      setCondition(params);
      conditionRef.current = params;
    } else {
      if (!validate()) {
        return;
      }
      conditionRef.current = condition;
    }
    if (conditionRef.current?.elmPrjId) {
      const aElmPrjId = conditionRef.current?.elmPrjId;
      // 단계유형별 설정가능 상태정보 조회 (프로젝트/단계설정 완료여부, 단계유형별 설비여부)
      const status = await refreshSetupStatus(aElmPrjId);
      // 단계설정 완료여부
      // 상태정보를 조회하지 못한 경우(시스템 오류 등)에는 완료로 간주하여 안내 모달을 띄우지 않음
      const setgCplt = (status || []).length < 1 || status[0]?.setgCpltYn !== 'N';

      // [26.08.06] 해당 프로젝트의 FA 담당자(본사/법인), FA_MANAGER만 수정가능
      let auth = false;
      if (hasRole('ADM') || hasRole('FA_MANAGER')) {
        auth = true;
      } else {
        auth = await checkAvailableProjectManager(aElmPrjId, 'HQCOP');
      }
      setHasAuth(auth);
      if (auth && !setgCplt) {
        // 단계유형이 없는 경우 셋업 단계 복사를 통한 셋업단계설정 유도
        const prjStepType = await findProjectStepType(aElmPrjId);
        if ((prjStepType || []).length < 1) {
          openCommonModal({
            modalType: 'confirm',
            content: t(
              'setup.label.기존 프로젝트를 참조해 단계유형/공통단계/셋업단계를 생성하시겠습니까?',
              '기존 프로젝트를 참조해 단계유형/공통단계/셋업단계를 생성하시겠습니까?'
            ),
            yesCallback: async () => {
              handleCopy();
            },
          });
        }
      }
    }
    searchTabContent();
  };

  const searchTabContent = () => {
    if ((tabMenus || []).length) {
      if (tabMenus[currentTabIndex].id === 'CMN') {
        tabCmnRef.current?.search(conditionRef.current);
      } else if (tabMenus[currentTabIndex].id === 'SP') {
        tabStepRef.current?.search(conditionRef.current);
      }
    }
  };

  const handleCopy = () => {
    if (conditionRef.current?.elmPrjId) {
      setOpenCopyPopup(true);
    } else {
      openMessageBar({
        type: 'warning',
        content: t(
          'setup.label.선택된 프로젝트 정보가 없습니다.',
          '선택된 프로젝트 정보가 없습니다.'
        ),
      });
    }
  };

  /**
   * 검증/설정완료 API 결과 확인
   * 두 API 모두 검증에 실패하더라도 정상응답(successOrNot: 'Y')이며
   * data에 오류 메시지 목록을 반환한다. (빈 배열인 경우 통과)
   * @returns 통과여부 (오류가 있는 경우 메시지 노출 후 false)
   */
  const checkResultMessages = (res) => {
    // API 자체가 실패한 경우
    if (res?.successOrNot !== 'Y') {
      openMessageBar({
        type: 'error',
        content: t('setup.label.설정 중 오류가 발생했습니다.', '설정 중 오류가 발생했습니다.'),
      });
      return false;
    }
    const messages = (Array.isArray(res?.data) ? res.data : []) as string[];
    if (messages.length > 0) {
      openMessageBar({ type: 'error', content: messages.join('<br/>') });
      return false;
    }
    return true;
  };

  /**
   * 설정 완료
   * 현재 탭의 수정내용을 저장한 뒤 전체 데이터 검증 후 설정완료 처리
   */
  const handleConfirm = async () => {
    const elmPrjId = conditionRef.current?.elmPrjId;
    if (!elmPrjId) {
      openMessageBar({
        type: 'warning',
        content: t(
          'setup.label.선택된 프로젝트 정보가 없습니다.',
          '선택된 프로젝트 정보가 없습니다.'
        ),
      });
      return;
    }

    // 현재 탭의 수정여부에 따라 확인 문구 분기
    const tabRef = tabMenus[currentTabIndex].id === 'CMN' ? tabCmnRef : tabStepRef;
    const isChanged = !!tabRef.current?.hasChanges?.();

    openCommonModal({
      modalType: 'confirm',
      content: isChanged
        ? t(
            'setup.label.변경된 내용이 있습니다.<br/>저장 후 설정을 완료하시겠습니까?',
            '변경된 내용이 있습니다.<br/>저장 후 설정을 완료하시겠습니까?'
          )
        : t('setup.label.설정을 완료하시겠습니까?', '설정을 완료하시겠습니까?'),
      yesCallback: async () => {
        // 1. 수정된 내용이 있는 경우 검증 후 저장
        if (isChanged) {
          const isSaved = await tabRef.current?.saveChanges();
          // 검증 실패 또는 저장 실패 (메시지는 각 탭에서 노출)
          if (!isSaved) {
            return;
          }
        }
        openLoading(true);
        try {
          // 2. 단계유형, 공통단계, 단계유형별 셋업단계 정상데이터 검증
          if (!checkResultMessages(await validateProjectStepSetup(elmPrjId))) {
            return;
          }

          // 3. 설정완료 처리
          if (!checkResultMessages(await completeProjectStepSetup(elmPrjId))) {
            return;
          }
          openMessageBar({
            type: 'confirm',
            content: t('setup.label.설정이 완료되었습니다.', '설정이 완료되었습니다.'),
          });
          handleSearch(elmPrjId);
        } finally {
          openLoading(false);
        }
      },
    });
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="twoCol">
              <SearchCols style={{ width: '30%' }}>
                <label>
                  <span>{String(t('setup.label.플랜트', '플랜트'))}</span>
                </label>
                <MultiComboBox
                  id="bildPlntCds"
                  options={code?.bildPlntCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(rowData || []).filter(
                        (o) =>
                          condition?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span className="dot">{String(t('setup.label.프로젝트명', '프로젝트명'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionsPrjMaster}
                  defaultValue={condition?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                  }}
                  isError={errors?.elmPrjId}
                  msgError={String(
                    t('setup.label.프로젝트명을 선택해 주세요.', '프로젝트명을 선택해 주세요.')
                  )}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={defaultCondition}
              setReset={setCondition}
              onSearch={() => handleSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>

      <div css={tabs.wrap} style={{ marginTop: '12px' }}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`setup-project-step-regist-tab-${idx}`}
              aria-controls={`setup-project-step-regist-tabpanel-${idx}`}
            />
          ))}
          {conditionRef.current?.elmPrjId &&
            (rowData || []).find((o) => o.elmPrjId === conditionRef.current?.elmPrjId)
              ?.elmPrjNm && (
              <SubTitleGroup style={{ height: '36px' }}>
                <div className="info primary">
                  {
                    (rowData || []).find((o) => o.elmPrjId === conditionRef.current?.elmPrjId)
                      ?.elmPrjNm
                  }
                </div>
              </SubTitleGroup>
            )}
        </Tabs>
        <div css={tabs.btn}>
          {/* [26.08.26] 단계설정이 완료된 경우 셋업 단계 복사/설정 완료 불가 */}
          {hasAuth && setgCpltYn === 'N' && (
            <>
              <Button css={IconButton.button} className="copy" onClick={handleCopy} disableRipple>
                {t('setup.label.셋업 단계 복사', '셋업 단계 복사')}
              </Button>
              <Button
                css={IconButton.button}
                className="confirm"
                onClick={handleConfirm}
                disableRipple
              >
                {t('setup.label.설정 완료', '설정 완료')}
              </Button>
            </>
          )}
        </div>
      </div>
      <TabPanel value={currentTabIndex} index={currentTabIndex}>
        {currentTabIndex === 0 ? (
          <CommonStepContent
            ref={tabCmnRef}
            isReadOnly={!hasAuth}
            orgTpCd={orgTpCd}
            setupStatus={setupStatus}
            onSaved={() => refreshSetupStatus()}
          />
        ) : (
          <SetupStepContent
            ref={tabStepRef}
            isReadOnly={!hasAuth}
            setupStatus={setupStatus}
            onRefreshStatus={() => refreshSetupStatus()}
          />
        )}
      </TabPanel>
      {isOpenCopyPopup && conditionRef.current?.elmPrjId && (
        <SetupStepCopyPopup
          open={isOpenCopyPopup}
          close={() => setOpenCopyPopup(false)}
          elmPrjId={conditionRef.current?.elmPrjId}
          onRefresh={() => handleSearch()}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`setup-project-step-regist-tabpanel-${index}`}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default SetupProjectStepManagementPage;
